/**
 * PPP per-item price display.
 *
 * Renders "($X.XX/ea)" or "($X.XX/person)" under the price.
 *
 * Survives quick-shop modal injection: uses document-body MutationObserver +
 * document-level event delegation, so it picks up any per-item-price element
 * that appears in the DOM later (not just elements present at page load).
 *
 * Prefers the deterministic data-variant-map embedded by the Liquid template
 * (variant_id -> {p: price_cents, n: pack_size}). Falls back to text-parsing
 * the visible price + reading the checked radio if the map is missing.
 */
(function () {
  'use strict';

  function getFormScope(perItemEl) {
    return perItemEl.closest('form') || document;
  }

  function getSelectedVariantId(scope) {
    // Impulse renders the variant id as <select name="id"> (no input[name=id]),
    // so query both — input-only here left the map path dead and the per-ea
    // label frozen at the first variant's rate (the "$23 at every tier" bug).
    var idInput = scope.querySelector ? scope.querySelector('input[name="id"], select[name="id"]') : null;
    if (!idInput || !idInput.value) {
      idInput = document.querySelector('input[name="id"], select[name="id"]');
    }
    if (idInput && idInput.value) return idInput.value;
    return null;
  }

  function getSelectedPackSizeFromRadio(scope) {
    var qs = scope.querySelector
      ? scope.querySelector('input[name="Pack Size"]:checked, input[name="Team Size"]:checked')
      : null;
    if (qs) {
      var m = (qs.value || '').match(/(\d+)/);
      if (m) return parseInt(m[1], 10);
    }
    return null;
  }

  function readSalePriceCents(scope) {
    var priceEl = scope.querySelector
      ? scope.querySelector('[data-product-price]:not([data-product-price-wrap])')
      : null;
    if (!priceEl) {
      priceEl = document.querySelector('[data-product-price]:not([data-product-price-wrap])');
    }
    if (!priceEl) return null;

    // Filter out compare-at children, then take the lowest $ amount.
    var parts = [];
    for (var i = 0; i < priceEl.childNodes.length; i++) {
      var n = priceEl.childNodes[i];
      if (n.nodeType === 3) {
        parts.push(n.textContent);
      } else if (n.nodeType === 1) {
        var isCompare = false;
        if (n.getAttribute) {
          if (n.getAttribute('data-compare-price') !== null) isCompare = true;
          if (n.getAttribute('data-product-price-wrap') !== null) isCompare = true;
        }
        var cls = (n.className && n.className.toString) ? n.className.toString() : '';
        if (/compare|strikethrough|product__price--compare|price--original/i.test(cls)) isCompare = true;
        var tag = (n.tagName || '').toLowerCase();
        if (tag === 's' || tag === 'del' || tag === 'strike') isCompare = true;
        if (!isCompare) parts.push(n.textContent || '');
      }
    }
    var text = parts.join(' ').replace(/Save\s*\$[\d,]+(?:\.\d+)?/gi, '');
    var matches = text.match(/\$\s*[\d,]+(?:\.\d{1,2})?/g);
    if (!matches || !matches.length) {
      // Fallback: walk full textContent, take lowest (sale <= compare-at)
      var all = (priceEl.textContent || '').replace(/Save\s*\$[\d,]+(?:\.\d+)?/gi, '');
      matches = all.match(/\$\s*[\d,]+(?:\.\d{1,2})?/g);
      if (!matches || !matches.length) return null;
    }
    var nums = matches
      .map(function (s) { return parseFloat(s.replace(/[\$,\s]/g, '')); })
      .filter(function (n) { return !isNaN(n) && n > 0; });
    if (!nums.length) return null;
    return Math.round(Math.min.apply(null, nums) * 100);
  }

  function computeFromMap(perItemEl, scope) {
    var raw = perItemEl.getAttribute('data-variant-map');
    if (!raw) return null;
    var map;
    try { map = JSON.parse(raw); } catch (e) { return null; }
    var id = getSelectedVariantId(scope);
    if (!id || !map[id]) return null;
    var v = map[id];
    if (!v || !v.n || v.n <= 0) return null;
    return { cents: v.p, pack: v.n };
  }

  function updateOne(perItemEl) {
    var scope = getFormScope(perItemEl);
    var data = computeFromMap(perItemEl, scope);

    var priceCents, pack;
    if (data) {
      priceCents = data.cents;
      pack = data.pack;
    } else {
      // Fallback: read visible price + checked radio
      priceCents = readSalePriceCents(scope);
      pack = getSelectedPackSizeFromRadio(scope) || getSelectedPackSizeFromRadio(document);
    }

    if (priceCents == null || !pack || pack <= 0) return;

    var perItem = (priceCents / 100 / pack).toFixed(2);
    var label = perItemEl.getAttribute('data-unit-label') || '/ea';
    var next = '$' + perItem + label;
    if (perItemEl.textContent !== next) {
      perItemEl.textContent = next;
    }
  }

  var raf = null;
  function updateAll() {
    if (raf) return;
    // Plain timeout, NOT requestAnimationFrame: rAF doesn't fire in hidden or
    // backgrounded tabs, which parks this guard on first call and freezes the
    // label until (if ever) the tab is foregrounded.
    raf = setTimeout(function () {
      raf = null;
      var els = document.querySelectorAll('#per-item-price, [id="per-item-price"]');
      for (var i = 0; i < els.length; i++) updateOne(els[i]);
    }, 16);
  }

  // After a variant change, Impulse syncs select[name=id] + the price DOM
  // asynchronously WITHOUT firing events or attribute mutations — fixed-delay
  // retries raced it and lost (the "$23 at every tier" bug). Poll briefly
  // instead: cheap, bounded, and immune to however late the theme lands.
  var pollTimer = null;
  function pollUpdates() {
    if (pollTimer) clearInterval(pollTimer);
    var ticks = 0;
    updateAll();
    pollTimer = setInterval(function () {
      ticks++;
      updateAll();
      if (ticks >= 20) { clearInterval(pollTimer); pollTimer = null; }
    }, 150);
  }

  // Document-level event delegation — survives modal injection.
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.matches) return;
    if (
      t.matches('input[name="Pack Size"]') ||
      t.matches('input[name="Team Size"]') ||
      t.matches('input[name="id"]') ||
      t.matches('select[name="id"]') ||
      t.matches('[data-variant-input]') ||
      t.matches('[data-product-select]')
    ) {
      pollUpdates();
    }
  }, true);

  // Body-level MutationObserver — catches: (a) modal injection of per-item-price
  // element, (b) Shopify variant.js mutating data-product-price, (c) form's
  // hidden [name="id"] value changing.
  function startBodyObserver() {
    if (!document.body) {
      document.addEventListener('DOMContentLoaded', startBodyObserver);
      return;
    }
    var observer = new MutationObserver(function (mutations) {
      var shouldUpdate = false;
      for (var i = 0; i < mutations.length && !shouldUpdate; i++) {
        var m = mutations[i];
        if (m.type === 'childList') {
          for (var j = 0; j < m.addedNodes.length; j++) {
            var n = m.addedNodes[j];
            if (n.nodeType !== 1) continue;
            if (n.id === 'per-item-price') { shouldUpdate = true; break; }
            if (n.querySelector && n.querySelector('#per-item-price')) { shouldUpdate = true; break; }
            // Theme re-renders replace the price node wholesale (childList, not
            // characterData) — catch those too.
            if (n.matches && n.matches('[data-product-price]')) { shouldUpdate = true; break; }
            if (n.querySelector && n.querySelector('[data-product-price]')) { shouldUpdate = true; break; }
          }
          if (!shouldUpdate && m.target && m.target.nodeType === 1 && m.target.closest &&
              m.target.closest('[data-product-price]')) { shouldUpdate = true; }
        } else if (m.type === 'characterData') {
          var p = m.target && m.target.parentNode;
          while (p && p.nodeType === 1) {
            if (p.matches && p.matches('[data-product-price]')) { shouldUpdate = true; break; }
            if (p.id === 'per-item-price') { p = null; break; } // ignore our own writes
            p = p.parentNode;
          }
        } else if (m.type === 'attributes' && m.attributeName === 'value') {
          if (m.target && m.target.matches && m.target.matches('input[name="id"]')) {
            shouldUpdate = true;
          }
        }
      }
      if (shouldUpdate) updateAll();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['value']
    });
  }

  function init() {
    updateAll();
    startBodyObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
